import { IMAGES } from "@/data/site";

// Every service uses the same clear structure so the site reads as one method.
export const PRACTICES = {
  Government: { to: "/government", label: "Government" },
  Political: { to: "/political", label: "Political" },
  Digital: { to: "/digital", label: "Digital" },
  Impact: { to: "/impact", label: "Impact & Intelligence" },
};

export const SERVICES = {
  // ---------------- GOVERNMENT ----------------
  "governance-transformation": {
    practice: "Government",
    title: "Governance Transformation",
    tagline: "Build institutions that can actually deliver.",
    image: IMAGES.abujaTower,
    what: "We help government institutions redesign how they are organised, so decisions turn into action instead of getting stuck.",
    problem: ["Responsibilities overlap and nobody clearly owns the result", "Decisions take too long", "Departments work in silos", "Processes are manual and slow", "It is unclear who is accountable for what"],
    does: ["Review how the institution really works today", "Redesign roles, teams and reporting lines", "Simplify and document key processes", "Set clear accountability for every priority", "Support the team through the change"],
    how: ["Diagnose", "Design", "Implement", "Embed", "Measure"],
    get: ["Institutional diagnostic", "New operating model", "Clear org structure", "Accountability (RACI) matrix", "Simple process maps and SOPs", "A transformation roadmap"],
    outcome: "Clearer responsibilities, faster decisions and an institution built to deliver.",
    whoFor: ["Ministries", "Agencies", "Governors' offices", "Permanent Secretaries"],
    related: ["programme-delivery", "monitoring-evaluation", "citizen-experience"],
  },
  "programme-delivery": {
    practice: "Government",
    title: "Programme & Project Delivery",
    tagline: "Turn priorities into coordinated action.",
    image: IMAGES.roadBillboard,
    what: "A policy announcement is only the start. We build the system that keeps every department, contractor and budget moving in the same direction.",
    problem: ["Jobs are not clearly assigned", "Agencies work separately", "Projects are not tracked the same way", "Delays are noticed too late", "Leaders cannot see real progress", "Reports show activity, not results"],
    does: ["Break priorities into clear workstreams and milestones", "Set up a delivery unit (PMO)", "Track projects, owners, budgets and risks", "Run regular delivery reviews", "Flag blockers early and escalate them", "Turn data into simple reports for leadership"],
    how: ["Map", "Structure", "Mobilise", "Track", "Escalate", "Improve"],
    get: ["Delivery framework", "PMO structure", "Live project tracker", "KPI framework", "Risk and issue registers", "Performance dashboard", "Executive reports"],
    outcome: "Leadership knows what is happening, teams know what they own, and problems surface early.",
    whoFor: ["Governors", "Ministers", "Commissioners", "Agencies", "Large public projects"],
    related: ["governance-transformation", "monitoring-evaluation", "executive-office"],
  },
  "policy-programme-design": {
    practice: "Government",
    title: "Policy & Programme Design",
    tagline: "Turn policy into something you can actually run.",
    image: IMAGES.conference,
    what: "A policy only matters when it becomes a programme with clear goals, owners, a budget and a way to measure success. We design that.",
    problem: ["Goals are not turned into practical steps", "It is unclear who benefits", "Agencies are not aligned", "Targets are hard to measure", "Budgets are disconnected from delivery"],
    does: ["Understand the problem the policy is solving", "Design the programme and who runs it", "Define beneficiaries and targets", "Build a clear implementation plan", "Set measurable KPIs from the start"],
    how: ["Research", "Design", "Structure", "Plan", "Measure"],
    get: ["Policy analysis", "Programme design", "Theory of change", "Implementation roadmap", "KPI framework", "Risk framework"],
    outcome: "A policy that can actually be implemented — and measured.",
    whoFor: ["Ministries", "Policy units", "Development partners"],
    related: ["programme-delivery", "monitoring-evaluation"],
  },
  "monitoring-evaluation": {
    practice: "Government",
    title: "Monitoring & Evaluation",
    tagline: "Know whether the programme is actually working.",
    image: IMAGES.cityRiver,
    what: "Monitoring tells you what is happening. Evaluation tells you whether it is working. We build systems that give leaders both.",
    problem: ["Reports measure activity, not change", "There is no baseline to compare against", "Data is scattered and late", "Nobody can say what actually improved"],
    does: ["Design clear indicators and targets", "Set baselines", "Build monitoring and reporting routines", "Measure real outcomes and impact", "Present findings in simple dashboards"],
    how: ["Inputs", "Activities", "Outputs", "Outcomes", "Impact"],
    get: ["M&E framework", "KPI dictionary", "Baseline study", "Live dashboard", "Evaluation and impact reports"],
    outcome: "Decisions based on evidence, not guesswork.",
    whoFor: ["Government programmes", "Agencies", "Development partners"],
    related: ["programme-delivery", "programme-analytics", "governance-intelligence"],
  },
  "citizen-experience": {
    practice: "Government",
    title: "Citizen Experience",
    tagline: "Design services around the people who use them.",
    image: IMAGES.classroom,
    what: "A citizen does not experience a policy — they experience a journey. We map that journey and remove the friction at every step.",
    problem: ["Too many steps to get a service", "Information is hard to find", "Long waits and no updates", "No easy way to complain or get help"],
    does: ["Map the full citizen journey", "Find where people get stuck", "Redesign the service to be simpler", "Set up feedback and complaint channels", "Add voice, WhatsApp and SMS where useful"],
    how: ["Discover", "Understand", "Apply", "Access", "Feedback", "Resolve"],
    get: ["Citizen journey map", "Service blueprint", "Experience audit", "Feedback system", "Grievance workflow", "Citizen dashboard"],
    outcome: "Simpler services, clearer communication and more trust.",
    whoFor: ["Service agencies", "Ministries", "State governments"],
    related: ["government-communications", "govx-citizen", "citizen-intelligence"],
  },
  "government-communications": {
    practice: "Government",
    title: "Government Communications",
    tagline: "Make government easy to understand.",
    image: IMAGES.radioTech,
    what: "A government can do something important and still fail to explain its value. We translate policies and programmes into language citizens understand.",
    problem: ["People do not know what was decided", "The benefit is not explained", "It is unclear how to access a service", "Delivered results are never communicated"],
    does: ["Build a clear communication strategy", "Run public information campaigns", "Create digital content, video and infographics", "Manage media relations", "Handle communication during a crisis"],
    how: ["What was decided", "Why it matters", "Who benefits", "How to access it", "What was delivered"],
    get: ["Communication strategy", "Campaign plans", "Content and creative", "Website and channels", "Media plan"],
    outcome: "From government language to citizen understanding.",
    whoFor: ["Governments", "Agencies", "Public programmes"],
    related: ["citizen-experience", "govx-citizen"],
  },
  "investment-economic-development": {
    practice: "Government",
    title: "Investment & Economic Development",
    tagline: "Turn a state's ambition into an investment pipeline.",
    image: IMAGES.lagosVIblue,
    what: "States have land, resources and incentives — but investors need a clear offer and an organised process. We build both.",
    problem: ["Opportunities are not packaged clearly", "It is hard for investors to engage", "Interest is not tracked", "Commitments stall before delivery"],
    does: ["Identify priority sectors", "Package investment opportunities", "Target the right investors", "Manage investor relationships", "Track commitments through to delivery"],
    how: ["Identify", "Package", "Target", "Engage", "Facilitate", "Track"],
    get: ["Sector opportunity maps", "Investor database", "Investment propositions", "Engagement dashboard"],
    outcome: "From investment ambition to a real, tracked pipeline.",
    whoFor: ["State governments", "Investment agencies", "Economic teams"],
    related: ["programme-delivery", "government-communications"],
  },

  // ---------------- POLITICAL ----------------
  "political-strategy": {
    practice: "Political",
    title: "Political Strategy",
    tagline: "Build the strategy behind the campaign.",
    image: IMAGES.conference,
    what: "Political organisations work in complex environments. We help set a clear direction across issues, message and stakeholders.",
    problem: ["Strong leaders but weak systems", "No clear position on the issues that matter", "Decisions made without evidence", "Message and organisation pull in different directions"],
    does: ["Define strategic positioning", "Run political and issue research", "Map key stakeholders", "Develop the platform and message", "Plan strategic communications"],
    how: ["Research", "Position", "Plan", "Align", "Execute"],
    get: ["Strategy document", "Issue and stakeholder maps", "Platform and messaging", "Communications plan"],
    outcome: "A clear, evidence-based direction the whole organisation can follow.",
    whoFor: ["Parties", "Candidates", "Public leaders"],
    related: ["organisation", "campaign-operations", "research-intelligence"],
  },
  "organisation": {
    practice: "Political",
    title: "Organisation",
    tagline: "Build the machinery behind the movement.",
    image: IMAGES.community,
    what: "Visibility is not enough. We build the structure, people and routines that let a political organisation actually run.",
    problem: ["No clear structure or roles", "Volunteers without coordination", "Weak links between national, state and LGA teams", "No way to measure performance"],
    does: ["Design the organisational structure", "Build membership and volunteer systems", "Set up state and LGA coordination", "Create internal communication routines", "Add simple performance tracking"],
    how: ["Assess", "Design", "Build", "Train", "Operate", "Measure"],
    get: ["Organisation design", "Membership and volunteer systems", "Coordination structure", "Performance system"],
    outcome: "A more coordinated, accountable and scalable organisation.",
    whoFor: ["Parties", "Political movements", "Campaigns"],
    related: ["political-strategy", "campaign-operations", "govx-political-platform"],
  },
  "campaign-operations": {
    practice: "Political",
    title: "Campaign Operations",
    tagline: "Run the campaign as an organisation, not a scramble.",
    image: IMAGES.womanSmiling,
    what: "We turn a campaign into a well-run operation where everyone knows what to do, who owns it and what happens next.",
    problem: ["Activities happen with no follow-through", "Events without a plan", "Field teams not coordinated", "No clear reporting to leadership"],
    does: ["Set up the campaign office and plan", "Build the calendar and task system", "Coordinate field and volunteer teams", "Run war-room operations", "Report progress clearly"],
    how: ["Strategy", "People", "Tasks", "Field", "Data", "Decision"],
    get: ["Campaign plan", "Workstreams and calendar", "Task and field systems", "War-room reporting"],
    outcome: "Everyone knows what needs to happen, who owns it and what comes next.",
    whoFor: ["Candidates", "Campaign teams", "Parties"],
    related: ["organisation", "political-communications", "transition"],
  },
  "political-communications": {
    practice: "Political",
    title: "Communications",
    tagline: "One clear voice across every channel.",
    image: IMAGES.techTeam,
    what: "We build the brand, content and channels a political organisation needs to communicate consistently and well.",
    problem: ["Inconsistent message", "Weak digital presence", "Slow, reactive content", "No media strategy"],
    does: ["Build the political brand and message", "Plan digital strategy", "Produce content, creative and video", "Manage media relations", "Build the website and digital platforms"],
    how: ["Brand", "Plan", "Create", "Publish", "Engage"],
    get: ["Brand and message guide", "Content plans", "Creative and video", "Website and channels"],
    outcome: "A consistent, professional voice everywhere the organisation shows up.",
    whoFor: ["Parties", "Candidates", "Public leaders"],
    related: ["political-strategy", "campaign-operations"],
  },
  "research-intelligence": {
    practice: "Political",
    title: "Research & Intelligence",
    tagline: "Understand the ground before you move.",
    image: IMAGES.manLaptop,
    what: "We provide structured research so political decisions are based on evidence, not assumptions.",
    problem: ["Decisions made on gut feel", "Little understanding of public concerns", "No read on the media narrative", "Unknown stakeholder positions"],
    does: ["Analyse the political landscape", "Run public opinion research", "Track issues and media", "Map stakeholders", "Review campaign performance"],
    how: ["Scope", "Gather", "Analyse", "Brief", "Recommend"],
    get: ["Intelligence briefs", "Issue trackers", "Stakeholder maps", "Research reports"],
    outcome: "Better decisions built on real evidence.",
    whoFor: ["Parties", "Candidates", "Strategy teams"],
    related: ["political-strategy", "governance-intelligence"],
  },
  "transition": {
    practice: "Political",
    title: "Transition",
    tagline: "From winning to governing.",
    image: IMAGES.abujaHill,
    what: "The period between winning and taking office is critical. We help turn a mandate into a governing agenda and a ready delivery team.",
    problem: ["Priorities are unclear on day one", "No plan for the first 100 days", "Campaign teams disappear", "No handover into government systems"],
    does: ["Set priorities and the policy agenda", "Map institutions and key people", "Build the first 100-day plan", "Design the delivery unit", "Set up executive reporting"],
    how: ["Priorities", "Institutions", "People", "Plan", "Deliver"],
    get: ["Transition plan", "100-day plan", "Delivery unit design", "Executive reporting setup"],
    outcome: "A structured move from political mandate to governing agenda.",
    whoFor: ["Incoming governments", "Transition teams"],
    related: ["campaign-operations", "programme-delivery", "executive-office"],
  },

  // ---------------- DIGITAL ----------------
  "govx-delivery": {
    practice: "Digital",
    title: "GovX Delivery",
    tagline: "Programme and project management for government.",
    image: IMAGES.coding,
    what: "A simple platform to plan work, assign owners, track progress and report — so delivery is visible in one place.",
    problem: ["Progress lives in scattered spreadsheets", "No single view for leaders", "Blockers surface too late"],
    does: ["Plan programmes and projects", "Assign owners and milestones", "Monitor progress and risks", "Escalate blockers", "Generate leadership reports"],
    how: ["Plan", "Assign", "Monitor", "Escalate", "Report"],
    get: ["Delivery platform setup", "Dashboards", "Automated reports"],
    outcome: "One clear, live view of delivery for the whole institution.",
    whoFor: ["Delivery units", "Agencies", "Governors' offices"],
    related: ["programme-delivery", "executive-office", "govx-intelligence"],
  },
  "govx-citizen": {
    practice: "Digital",
    title: "GovX Citizen",
    tagline: "Citizen engagement and feedback infrastructure.",
    image: IMAGES.radioTech,
    what: "Reach citizens where they are — voice, WhatsApp and SMS — to inform them, answer them and capture their feedback.",
    problem: ["Programmes do not reach eligible citizens", "No structured way to collect feedback", "Complaints get lost"],
    does: ["Send information and reminders", "Answer common questions", "Collect feedback at scale", "Route complaints to the right team", "Measure engagement"],
    how: ["Listen", "Inform", "Respond", "Measure"],
    get: ["Outreach workflows", "Multilingual scripts", "Feedback dashboards", "Escalation workflows"],
    outcome: "More citizens reached, better information and measurable engagement.",
    whoFor: ["Service agencies", "Public programmes"],
    related: ["citizen-experience", "citizen-intelligence"],
  },
  "govx-political-platform": {
    practice: "Digital",
    title: "GovX Political",
    tagline: "Digital infrastructure for political organisations.",
    image: IMAGES.groupCouch,
    what: "The tools a political organisation needs to organise members, communicate and coordinate the field — in one place.",
    problem: ["Member and volunteer data is scattered", "No easy way to mobilise the field", "Communication is slow"],
    does: ["Organise members and volunteers", "Communicate across channels", "Coordinate and mobilise the field", "Measure activity"],
    how: ["Organise", "Communicate", "Mobilise", "Measure"],
    get: ["Membership platform", "Communication tools", "Field coordination dashboards"],
    outcome: "A coordinated organisation that can move quickly.",
    whoFor: ["Parties", "Campaigns", "Movements"],
    related: ["organisation", "campaign-operations"],
  },
  "govx-intelligence": {
    practice: "Digital",
    title: "GovX Intelligence",
    tagline: "Governance and political intelligence.",
    image: IMAGES.manLaptop,
    what: "Track policies, institutions and developments, and turn them into clear, decision-ready analysis.",
    problem: ["Information is fragmented", "Hard to see trends", "Analysis arrives too late to act"],
    does: ["Track relevant developments", "Analyse trends and issues", "Turn data into clear briefs", "Support timely decisions"],
    how: ["Track", "Analyse", "Understand", "Act"],
    get: ["Intelligence dashboards", "Regular briefs", "Trend analysis"],
    outcome: "Understanding you can act on, on time.",
    whoFor: ["Leadership teams", "Strategy units"],
    related: ["research-intelligence", "governance-intelligence"],
  },
  "digital-government": {
    practice: "Digital",
    title: "Digital Government",
    tagline: "Technology that removes friction, not adds it.",
    image: IMAGES.techTeam,
    what: "We help institutions decide where technology genuinely improves operations and services — then build it.",
    problem: ["Manual processes slow everything down", "Systems do not talk to each other", "Technology bought without a clear problem to solve"],
    does: ["Set a practical digital strategy", "Digitise the right processes", "Build platforms staff and citizens use", "Add AI where it creates real value"],
    how: ["Strategy", "Digitise", "Build", "Integrate", "Improve"],
    get: ["Digital roadmap", "Platforms and dashboards", "Workflow and CRM systems", "Integration plan"],
    outcome: "Simpler operations, better information and better services.",
    whoFor: ["Ministries", "Agencies", "State governments"],
    related: ["govx-delivery", "citizen-experience"],
  },
  "executive-office": {
    practice: "Digital",
    title: "GovX Executive",
    tagline: "Give leadership a clear view of what matters.",
    image: IMAGES.boardroom,
    what: "A focused view for Governors, Ministers and institutional leaders: what matters, what is moving, what is stuck and what needs a decision.",
    problem: ["Leaders spend time chasing updates", "Priorities get lost in day-to-day noise", "Decisions and follow-ups slip"],
    does: ["Track priorities and decisions", "Follow up on meetings and commitments", "Monitor key programmes", "Provide executive briefings"],
    how: ["What matters", "What's moving", "What's stuck", "What needs a decision", "What changed"],
    get: ["Executive dashboard", "Decision tracker", "Briefing system"],
    outcome: "Leadership time spent deciding, not chasing.",
    whoFor: ["Governors", "Ministers", "Commissioners", "Mayors"],
    related: ["programme-delivery", "govx-delivery"],
  },

  // ---------------- IMPACT ----------------
  "governance-intelligence": {
    practice: "Impact",
    title: "Governance Intelligence",
    tagline: "Track the institutions and programmes that matter.",
    image: IMAGES.cityRiver,
    what: "We monitor policies, institutions and public-sector developments and turn them into clear, useful analysis.",
    problem: ["Developments are hard to follow", "No single, reliable picture", "Analysis is not decision-ready"],
    does: ["Track policies and programmes", "Monitor institutional change", "Analyse what it means", "Brief decision-makers"],
    how: ["Track", "Analyse", "Interpret", "Brief"],
    get: ["Governance dashboards", "Regular briefs", "Analysis reports"],
    outcome: "A reliable, current picture of the landscape you operate in.",
    whoFor: ["Government", "Investors", "Development partners"],
    related: ["research", "programme-analytics"],
  },
  "impact-monitoring-evaluation": {
    practice: "Impact",
    title: "Monitoring & Evaluation",
    tagline: "Measure implementation against what was promised.",
    image: IMAGES.kadunaAerial,
    what: "We measure whether programmes are delivering the outcomes they were designed for, across the full results chain.",
    problem: ["Only activity is measured", "No baseline", "No clear read on impact"],
    does: ["Design indicators and baselines", "Monitor implementation", "Evaluate outcomes and impact", "Report in clear dashboards"],
    how: ["Baseline", "Monitor", "Evaluate", "Report"],
    get: ["M&E framework", "Dashboards", "Evaluation reports"],
    outcome: "A clear answer to the question: is it working?",
    whoFor: ["Programmes", "Agencies", "Development partners"],
    related: ["programme-analytics", "delivery-index"],
  },
  "citizen-intelligence": {
    practice: "Impact",
    title: "Citizen Intelligence",
    tagline: "Understand citizen experience and service gaps.",
    image: IMAGES.womanSmiling,
    what: "We gather citizen feedback and experience data to show where services work well and where they fall short.",
    problem: ["No structured citizen feedback", "Service gaps are invisible", "Decisions ignore citizen reality"],
    does: ["Collect feedback at scale", "Analyse experience and sentiment", "Highlight service gaps", "Feed findings back to leaders"],
    how: ["Collect", "Analyse", "Surface", "Act"],
    get: ["Feedback datasets", "Experience dashboards", "Insight reports"],
    outcome: "Decisions grounded in what citizens actually experience.",
    whoFor: ["Service agencies", "State governments"],
    related: ["citizen-experience", "govx-citizen"],
  },
  "programme-analytics": {
    practice: "Impact",
    title: "Programme Analytics",
    tagline: "Turn scattered project data into decisions.",
    image: IMAGES.coding,
    what: "We pull fragmented project information together into clean, decision-ready dashboards.",
    problem: ["Data lives in many places", "Reports are slow to build", "Leaders lack a live view"],
    does: ["Bring data sources together", "Build clear dashboards", "Automate reporting", "Surface risks and trends"],
    how: ["Collect", "Clean", "Visualise", "Report"],
    get: ["Unified dashboards", "Automated reports", "Data pipeline setup"],
    outcome: "One clean, live view of programme performance.",
    whoFor: ["Delivery units", "Agencies", "Programmes"],
    related: ["monitoring-evaluation", "delivery-index"],
  },
  "research": {
    practice: "Impact",
    title: "Research",
    tagline: "Policy, institutional and public-interest research.",
    image: IMAGES.manSuit,
    what: "Rigorous research to inform policy, programme and institutional decisions.",
    problem: ["Decisions lack an evidence base", "No clear read on the problem", "Assumptions go untested"],
    does: ["Scope the research question", "Gather and analyse evidence", "Produce clear findings", "Recommend practical action"],
    how: ["Scope", "Gather", "Analyse", "Recommend"],
    get: ["Research reports", "Data and findings", "Recommendations"],
    outcome: "Clear evidence that makes decisions easier.",
    whoFor: ["Government", "Development partners", "Institutions"],
    related: ["governance-intelligence", "delivery-index"],
  },
  "delivery-index": {
    practice: "Impact",
    title: "GovX Delivery Index",
    tagline: "How well is a programme actually being delivered?",
    image: IMAGES.lagosBW,
    what: "A structured framework that scores delivery across seven dimensions, so you can see exactly where implementation is strong and where it is breaking down.",
    problem: ["'Active' on paper but behind in reality", "No shared way to judge delivery health", "Weak spots are hard to pinpoint"],
    does: ["Assess planning, funding and capacity", "Assess execution and monitoring", "Assess citizen experience and outcomes", "Show it all in one clear view"],
    how: ["Planning", "Funding", "Capacity", "Execution", "Monitoring", "Citizen Experience", "Outcomes"],
    get: ["Delivery Index assessment", "Dimension scorecards", "Improvement priorities"],
    outcome: "A shared, honest picture of delivery health — and where to act first.",
    whoFor: ["Delivery units", "Leadership", "Development partners"],
    related: ["programme-delivery", "monitoring-evaluation"],
  },
};

// Ordered slugs per practice (drives the capability grids on each page)
export const PRACTICE_SERVICES = {
  Government: ["governance-transformation", "programme-delivery", "policy-programme-design", "monitoring-evaluation", "citizen-experience", "government-communications", "investment-economic-development"],
  Political: ["political-strategy", "organisation", "campaign-operations", "political-communications", "research-intelligence", "transition"],
  Digital: ["govx-delivery", "govx-citizen", "govx-political-platform", "govx-intelligence", "digital-government", "executive-office"],
  Impact: ["governance-intelligence", "impact-monitoring-evaluation", "citizen-intelligence", "programme-analytics", "research", "delivery-index"],
};
